import { useEffect, useRef } from 'react'
import Button from '../common/Button'
import StepTrail from './StepTrail'
import { useWizard } from '../../context/WizardContext'
import { ACTIONS, MESSAGES, copyFor } from '../../utils/copy'

/**
 * The frame every step sits in: the question, the answer, and the way forward.
 *
 * Identical on all nine screens on purpose. The customer this is for learns the shape
 * once — *question at the top, one thing to answer, big button at the bottom* — and then
 * never has to learn it again. A screen that rearranges itself is a screen that has to be
 * read from scratch.
 */
export default function WizardShell({ children, footer }) {
  const { steps, index, step, isFirst, isOptional, isSummary, submitError, next, back, skip } = useWizard()
  const copy = copyFor(step)
  const heading = useRef(null)

  /**
   * Focus lands on the new question, not back at the top of the document.
   *
   * Without this a screen-reader user hears nothing at all when the step changes — the
   * page did not navigate, so no announcement is made — and a keyboard user tabs from the
   * old page position into whatever happens to be there now.
   */
  useEffect(() => {
    heading.current?.focus()
  }, [step])

  const onSubmit = (event) => {
    event.preventDefault()
    if (!isSummary) next()
  }

  return (
    <form onSubmit={onSubmit} noValidate>
      <StepTrail steps={steps} index={index} />

      <h1
        ref={heading}
        tabIndex={-1}
        className="mt-3 font-display font-bold text-display-l text-ink outline-none"
      >
        {copy.title}
      </h1>

      {copy.hint && <p className="mt-1.5 text-body-s text-ink-3 max-w-[46ch]">{copy.hint}</p>}

      <div className="mt-5">{children}</div>

      {submitError && (
        <p role="alert" className="mt-4 text-body-s text-alert">
          {submitError.message || MESSAGES.submitFailed}
        </p>
      )}

      <div className="flex gap-3 mt-6">
        {!isFirst && (
          <Button type="button" variant="ghost" onClick={back} className="flex-none">
            {ACTIONS.back}
          </Button>
        )}

        {/* The skippable steps give Skip the same height as Continue.
            A skip drawn as a small grey link is a skip people do not find, and both of
            these steps are ones most customers should be passing straight through. */}
        {isOptional && (
          <Button type="button" variant="ghost" onClick={skip} className="flex-1">
            {ACTIONS.skip}
          </Button>
        )}

        {footer ?? (
          <Button type="submit" className="flex-1">
            {ACTIONS.next}
          </Button>
        )}
      </div>
    </form>
  )
}
